
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Trophy, Medal, Users, Star, Crown, UserPlus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

/**
 * Interface for leaderboard data from the database view
 */
interface LeaderboardEntry {
  user_id: string;
  username: string;
  points: number;
  rank: number;
  avatar_url: string | null;
  is_friend: boolean;
}

/**
 * Leaderboards Component
 * 
 * Displays leaderboards with real user data from the database.
 * Shows overall rankings and friends rankings with the ability to add friends.
 */
const Leaderboards = () => {
  // State to store leaderboard data
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Function to fetch real leaderboard data from Supabase
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        setIsLoading(true);
        console.log('Fetching real leaderboard data from leaderboard_view...');
        
        // Fetch real leaderboard data from the database view
        const { data, error } = await supabase
          .from('leaderboard_view')
          .select('*')
          .order('rank', { ascending: true });
        
        if (error) {
          console.error('Error fetching leaderboard data:', error);
          toast({
            title: "Error loading leaderboard",
            description: "Could not load leaderboard data. Please try again later.",
            variant: "destructive",
          });
          return;
        }
        
        // Transform the data to match our interface
        const transformedData: LeaderboardEntry[] = data?.map(item => ({
          user_id: item.user_id || '',
          username: item.username || '',
          points: item.points || 0,
          rank: item.rank || 0,
          avatar_url: item.avatar_url,
          is_friend: item.is_friend || false
        })) || [];
        
        // Set leaderboard data to state
        setLeaderboardData(transformedData);
        console.log('Fetched real leaderboard data:', transformedData);
      } catch (error) {
        console.error('Error in fetchLeaderboardData:', error);
        toast({
          title: "Error loading leaderboard",
          description: "Could not load leaderboard data. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchLeaderboardData();
  }, [toast]);

  // Filter friends from leaderboard data
  const friendsData = leaderboardData.filter(entry => entry.is_friend);

  /**
   * Navigate to friends page to add friends
   */
  const handleAddFriends = () => {
    navigate('/friends');
  };

  /**
   * Renders a single leaderboard entry with proper styling based on rank
   */
  const renderLeaderboardEntry = (entry: LeaderboardEntry, index: number) => {
    // Style based on rank
    const isTopThree = entry.rank <= 3;
    
    // Custom styling for top 3 ranks with icons
    const getRankBadge = (rank: number) => {
      switch (rank) {
        case 1:
          return (
            <div className="flex items-center justify-center p-2 bg-yellow-100 text-yellow-800 rounded-full w-8 h-8">
              <Crown className="w-4 h-4" />
            </div>
          );
        case 2:
          return (
            <div className="flex items-center justify-center p-2 bg-gray-200 text-gray-700 rounded-full w-8 h-8">
              <Medal className="w-4 h-4" />
            </div>
          );
        case 3:
          return (
            <div className="flex items-center justify-center p-2 bg-amber-100 text-amber-800 rounded-full w-8 h-8">
              <Medal className="w-4 h-4" />
            </div>
          );
        default:
          return (
            <div className="flex items-center justify-center p-2 bg-gray-100 text-gray-600 rounded-full w-8 h-8">
              {rank}
            </div>
          );
      }
    };

    return (
      <div 
        key={entry.user_id || index}
        className={`p-4 flex items-center justify-between ${
          isTopThree ? 'bg-primary/5 border-l-4 border-primary rounded-lg' : 'border-b'
        } ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
      >
        <div className="flex items-center gap-3">
          {/* Rank indicator with special styling for top 3 */}
          {getRankBadge(entry.rank)}
          
          {/* Username with friend indicator if applicable */}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium">{entry.username}</span>
              {entry.is_friend && (
                <Badge variant="secondary" className="text-xs">
                  <Users className="h-3 w-3 mr-1" />
                  Friend
                </Badge>
              )}
            </div>
            
            {/* Points display with star icon */}
            <div className="text-sm text-gray-500 flex items-center">
              <Star className="h-3 w-3 mr-1 text-yellow-500 fill-yellow-500" />
              {entry.points} points
            </div>
          </div>
        </div>
        
        {/* Progress indicator relative to top score */}
        <div className="w-24 md:w-32">
          <Progress 
            value={leaderboardData.length > 0 ? (entry.points / (leaderboardData[0]?.points || 1)) * 100 : 0} 
            className="h-2"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-900">Leaderboards</h1>
        {/* Add Friends Button */}
        <Button onClick={handleAddFriends} className="flex items-center gap-2">
          <UserPlus className="h-4 w-4" />
          Manage Friends
        </Button>
      </div>

      {isLoading ? (
        // Loading state with animated placeholders
        <div className="grid gap-6">
          <Card className="bg-white rounded-xl shadow-sm border border-gray-100">
            <CardHeader className="p-6 border-b border-gray-100">
              <CardTitle className="text-2xl font-semibold text-gray-900">Loading leaderboards...</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-16 bg-gray-100 rounded-lg"></div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        // Tabs for different leaderboard views
        <Tabs defaultValue="overall" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="overall" className="text-sm">
              <Trophy className="h-4 w-4 mr-2" />
              Overall Rankings
            </TabsTrigger>
            <TabsTrigger value="friends" className="text-sm">
              <Users className="h-4 w-4 mr-2" />
              Friends Rankings ({friendsData.length})
            </TabsTrigger>
          </TabsList>
          
          {/* Overall Rankings Tab - shows all users */}
          <TabsContent value="overall" className="mt-0">
            <Card className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <CardHeader className="p-6 border-b border-gray-100">
                <CardTitle className="text-2xl font-semibold text-gray-900">
                  Top Learners
                </CardTitle>
                <p className="text-gray-600 mt-1">
                  The most accomplished learners on the platform
                </p>
              </CardHeader>
              
              <CardContent className="p-0">
                <div className="divide-y">
                  {leaderboardData.map((entry, index) => renderLeaderboardEntry(entry, index))}
                </div>
                
                {leaderboardData.length === 0 && (
                  <div className="p-6 text-center text-gray-500">
                    <p>No learners on the leaderboard yet. Complete some lessons to be the first!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Friends Rankings Tab - shows only friends */}
          <TabsContent value="friends" className="mt-0">
            <Card className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <CardHeader className="p-6 border-b border-gray-100">
                <CardTitle className="text-2xl font-semibold text-gray-900">
                  Your Friends
                </CardTitle>
                <p className="text-gray-600 mt-1">
                  See how your friends are ranking on the leaderboard
                </p>
              </CardHeader>
              
              <CardContent className="p-0">
                <div className="divide-y">
                  {friendsData.map((entry, index) => renderLeaderboardEntry(entry, index))}
                </div>
                
                {friendsData.length === 0 && (
                  <div className="p-6 text-center text-gray-500">
                    <div className="max-w-md mx-auto">
                      <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                      <h3 className="text-lg font-medium mb-2">No friends on the leaderboard</h3>
                      <p className="mb-4">Add friends to see how you rank against them!</p>
                      <Button onClick={handleAddFriends} className="flex items-center gap-2 mx-auto">
                        <UserPlus className="h-4 w-4" />
                        Add Friends
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default Leaderboards;
